/** A fresh form for one run of the wizard. */
const emptyFormData = () => ({
  name: '',
  description: '',
  connectionData: null,
  // Per-file dataset names (one per uploaded file)
  fileDatasetNames: [],
  // When true, all files are grouped into one dataset
  groupAsOneDataset: false,
  // Dataset name used when groupAsOneDataset is true
  groupedDatasetName: '',
});

/**
 * Model for Data Source Wizard state
 */
export class DataSourceWizardModel {
  constructor() {
    this.isOpen = false;
    this.currentStep = 1;
    this.totalSteps = 3;
    this.listeners = [];
    this.resetRun();
  }

  /**
   * Clear everything belonging to ONE run of the wizard, leaving open/step and
   * subscribers alone.
   *
   * PRD-1359 (feedback doc §Creating a New Dataset): "I've already added a new
   * dataset, when returning to the New Dataset screen my old dataset's files
   * are shown as uploaded there." This model is created once in App.js and
   * lives for the session, but neither open() nor close() cleared the run — so
   * a second visit opened on the first run's files, names and dataset ids, and
   * uploading again would have attached them to the wrong dataset.
   */
  resetRun() {
    this.datasetId = null;
    // datasetIds holds all created dataset IDs (>1 when not grouped)
    this.datasetIds = [];
    this.jobId = null;
    this.formData = emptyFormData();
    this.selectedFiles = [];
    this.promptValidated = false;
    this.validationFeedback = null;
  }

  /**
   * Cleared on OPEN rather than on close: closing mid-upload should not pull
   * state out from under anything still rendering or still in flight, and a
   * reopen is the moment the user asks for a blank slate. Every caller opens at
   * step 1 — if a resume path is ever added it needs its own entry point, not a
   * step argument to this one.
   */
  open(step = 1) {
    this.resetRun();
    this.isOpen = true;
    this.currentStep = step;
    this.notify();
  }

  close() {
    this.isOpen = false;
    this.notify();
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.notify();
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.notify();
    }
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  setDatasetId(datasetId) {
    this.datasetId = datasetId;
    this.notify();
  }

  setDatasetIds(ids) {
    this.datasetIds = ids;
    // Keep datasetId as the first for backward-compat with single-dataset flow
    this.datasetId = ids.length > 0 ? ids[0] : null;
    this.notify();
  }

  setJobId(jobId) {
    this.jobId = jobId;
    this.notify();
  }

  setPromptValidated(validated) {
    this.promptValidated = validated;
    this.notify();
  }

  setValidationFeedback(feedback) {
    this.validationFeedback = feedback;
    this.notify();
  }

  setFormData(formData) {
    this.formData = { ...this.formData, ...formData };
    this.notify();
  }

  setSelectedFiles(files) {
    this.selectedFiles = files;
    this.notify();
  }

  clearFormData() {
    this.formData = {
      name: '',
      description: '',
      connectionData: null,
      fileDatasetNames: [],
      groupAsOneDataset: false,
      groupedDatasetName: '',
    };
    this.selectedFiles = [];
    this.datasetId = null;
    this.datasetIds = [];
    this.jobId = null;
    this.promptValidated = false;
    this.validationFeedback = null;
    this.notify();
  }

  notify() {
    this.listeners.forEach(listener =>
      listener({
        isOpen: this.isOpen,
        currentStep: this.currentStep,
        totalSteps: this.totalSteps,
        datasetId: this.datasetId,
        datasetIds: this.datasetIds,
        jobId: this.jobId,
        formData: this.formData,
        selectedFiles: this.selectedFiles,
        promptValidated: this.promptValidated,
        validationFeedback: this.validationFeedback,
      })
    );
  }

  getState() {
    return {
      isOpen: this.isOpen,
      currentStep: this.currentStep,
      totalSteps: this.totalSteps,
      datasetId: this.datasetId,
      datasetIds: this.datasetIds,
      jobId: this.jobId,
      formData: this.formData,
      selectedFiles: this.selectedFiles,
      promptValidated: this.promptValidated,
      validationFeedback: this.validationFeedback,
    };
  }
}
